import React, { useState, useEffect } from "react";
import {
  Typography,
  Select,
  MenuItem,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  Button,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Checkbox,
  CircularProgress,
} from "@material-ui/core";
import { ChevronLeft, Cached, Search, Clear } from "@material-ui/icons";
import moment from "moment";
import clsx from "clsx";
import { useHistory, useParams } from "react-router-dom";
import PaginationInput from "../../../models/pagination/PaginationInput";
import { fetchProjectDetailsAPI, fetchProjectHistoryAPI } from "./projectHistoryAPI";
import { useStyles } from "./ProjectHistoryStyles";
import { fetchAllDashboardFilters, saveDashboardFilters, extractDashboardFilters } from "../../../../../containers/utils/filterUtils";

const ProjectHistory = () => {
  const classes = useStyles();
  const history = useHistory();
  const { projectId } = useParams();
  
  // Filter states
  const [searchCriteria, setSearchCriteria] = useState("all");
  const [searchValue, setSearchValue] = useState("");
  
  // Data states
  const [projectDetailsData, setProjectDetailsData] = useState(null);
  const [historyEvents, setHistoryEvents] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // UI states
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastSyncTime, setLastSyncTime] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState(null);
  const [filtersLoading, setFiltersLoading] = useState(false);

  // Key used in /user/fetchAllFilters and /user/saveFilters payloads
  // {
  //   "projecthistory": { searchCriteria: "event", searchValue: "Confirmed" }
  // }
  const DASHBOARD_FILTER_KEY = "projecthistory";

  // Months list for dropdown
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  // Load project details on mount
  useEffect(() => {
    if (projectId) {
      loadProjectDetails();
    }
  }, [projectId]);

  // Load project details for ribbon
  const loadProjectDetails = async () => {
    try {
      setLoadingDetails(true);
      setError(null);
      const response = await fetchProjectDetailsAPI(projectId);
      if (response?.status && response?.data) {
        setProjectDetailsData(response.data);
      }
    } catch (err) {
      console.error("Error loading project details:", err);
      setError("Failed to load project details");
    } finally {
      setLoadingDetails(false);
    }
  };

  // Load project history with filters (optionally override params)
  const loadProjectHistory = async (overrideParams) => {
    if (!projectId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        projectId: projectId,
        searchCriteria: overrideParams?.searchCriteria || searchCriteria,
        searchValue: overrideParams?.searchValue ?? searchValue,
        pageNumber: overrideParams?.pageNumber || currentPage,
        pageSize: overrideParams?.pageSize || rowsPerPage,
      };

      const response = await fetchProjectHistoryAPI(params);
      
      if (response?.status && response?.data) {
        setHistoryEvents(response.data.projectHistory || []);
        setTotalRecords(response.data.totalRecords || 0);
        setTotalPages(response.data.totalPages || 0);
      }
    } catch (err) {
      console.error("Error loading project history:", err);
      setError("Failed to load project history");
      setHistoryEvents([]);
      setTotalRecords(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  // Initialize filters from saved dashboard filters, then load history
  useEffect(() => {
    if (!projectId) return;

    const initializeFiltersAndHistory = async () => {
      try {
        setFiltersLoading(true);

        const allFilters = await fetchAllDashboardFilters();
        const extracted = extractDashboardFilters(allFilters, DASHBOARD_FILTER_KEY);

        // Defaults
        let criteria = "all";
        let value = "";

        if (extracted?.status && extracted?.save_filter_options) {
          const saved = extracted.save_filter_options;
          criteria = saved.searchCriteria || "all";
          value = saved.searchValue || "";
        }

        // Apply to state (pagination uses defaults on load)
        setSearchCriteria(criteria);
        setSearchValue(value);
        setCurrentPage(1);
        setRowsPerPage(10);

        // Load history with these filters
        await loadProjectHistory({
          searchCriteria: criteria,
          searchValue: value,
          pageNumber: 1,
          pageSize: 10,
        });
      } catch (err) {
        console.error("Error initializing project history filters:", err);
      } finally {
        setFiltersLoading(false);
      }
    };

    initializeFiltersAndHistory();
  }, [projectId]);

  // Handle search button click
  const handleSearch = () => {
    setCurrentPage(1); // Reset to first page
    loadProjectHistory();
  };

  // Handle refresh
  const handleRefresh = () => {
    setLastSyncTime(new Date());
    loadProjectDetails();
    loadProjectHistory();
  };

  // Handle search criteria change
  const handleSearchCriteriaChange = (e) => {
    const newCriteria = e.target.value;
    setSearchCriteria(newCriteria);
    setSearchValue(""); // Clear search value
  };

  // Month search uses text input only
  const handleMonthInputChange = (value) => {
    setSearchValue(value);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (e) => {
    const newRowsPerPage = parseInt(e.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
    // Reload data with new page size
    setTimeout(() => {
      loadProjectHistory();
    }, 0);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Reload data will be triggered by useEffect
  };

  // Reload history when page changes
  useEffect(() => {
    if (projectId) {
      loadProjectHistory();
    }
  }, [currentPage, rowsPerPage]);

  // Handle save filters (uses unified dashboard filters API)
  const handleSaveFilters = async () => {
    try {
      const filterData = {
        searchCriteria,
        searchValue,
      };

      const result = await saveDashboardFilters(DASHBOARD_FILTER_KEY, filterData);
      console.log("Project history filters saved:", result);
    } catch (err) {
      console.error("Error saving project history filters:", err);
    }
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setSearchCriteria("all");
    setSearchValue("");
    setCurrentPage(1);
    // Reload with cleared filters
    setTimeout(() => {
      loadProjectHistory();
    }, 0);
  };

  const handleBack = () => {
    history.goBack();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Confirmed":
      case "Completed":
      case "Ready for Dispatch":
      case "COA Approved":
        return "#4CAF50"; // green
      case "Changes Under Review":
      case "In-Progress":
      case "Under Confirmation":
      case "Awaiting COA Approval":
      case "Ready for Production":
        return "#FF9800"; // orange
      case "On Hold":
        return "#9C27B0"; // purple
      case "Resubmit COA":
        return "#F44336"; // red
      case "Created":
      case "Planned":
      case "Scheduled":
        return "#2196F3"; // blue
      default:
        return "#757575"; // grey
    }
  };

  return (
    <div className={classes.root}>
      {/* Back Button */}
      <div className={classes.backButton} onClick={handleBack}>
        <ChevronLeft />
        <Typography variant="body1">Back</Typography>
      </div>

      {/* Project Details Section */}
      <div className={classes.projectDetailsContainer}>
        <div className={classes.projectDetailsTableWrapper}>
          <TableContainer component={Paper} style={{ boxShadow: "none", backgroundColor: "transparent" }}>
            <Table className={classes.projectDetailsTable}>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.projectDetailsHeader}>Project ID</TableCell>
                  <TableCell className={classes.projectDetailsHeader}>PO Number</TableCell>
                  <TableCell className={classes.projectDetailsHeader}>MBR Number</TableCell>
                  <TableCell className={classes.projectDetailsHeader}>Product Description</TableCell>
                  <TableCell className={classes.projectDetailsHeader}>CMO - Site</TableCell>
                  <TableCell className={classes.projectDetailsHeader}>Batch Number(s)</TableCell>
                  <TableCell className={classes.projectDetailsHeader}>Delivery Number</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell className={classes.projectDetailsValue}>
                    {loadingDetails ? (
                      <CircularProgress size={20} />
                    ) : (
                      projectDetailsData?.projectId || projectId || "N/A"
                    )}
                  </TableCell>
                  <TableCell className={classes.projectDetailsValue}>
                    {loadingDetails ? (
                      <CircularProgress size={20} />
                    ) : (
                      projectDetailsData?.poNumber || "N/A"
                    )}
                  </TableCell>
                  <TableCell className={classes.projectDetailsValue}>
                    {loadingDetails ? (
                      <CircularProgress size={20} />
                    ) : (
                      projectDetailsData?.mbrNumber || "N/A"
                    )}
                  </TableCell>
                  <TableCell className={classes.projectDetailsValue}>
                    {loadingDetails ? (
                      <CircularProgress size={20} />
                    ) : (
                      projectDetailsData?.productDescription || "N/A"
                    )}
                  </TableCell>
                  <TableCell className={classes.projectDetailsValue}>
                    {loadingDetails ? (
                      <CircularProgress size={20} />
                    ) : (
                      projectDetailsData?.cmoSite || "N/A"
                    )}
                  </TableCell>
                  <TableCell className={classes.projectDetailsValue}>
                    {loadingDetails ? (
                      <CircularProgress size={20} />
                    ) : (
                      projectDetailsData?.batchNumbers || "N/A"
                    )}
                  </TableCell>
                  <TableCell className={classes.projectDetailsValue}>
                    {loadingDetails ? (
                      <CircularProgress size={20} />
                    ) : (
                      projectDetailsData?.deliveryNumber || "N/A"
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>

      {/* Search and Refresh Section */}
      <Grid
        container
        style={{
          display: "flex",
          width: "100%",
          alignItems: "center",
          flexWrap: "nowrap",
          marginTop: "5px",
          marginBottom: "15px",
          gap: "1rem",
        }}
      >
        {/* Left Side - Search, Save Filters, Clear All Filters */}
        <Grid
          item
          xs={12}
          sm={8}
          md={8}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexWrap: "wrap",
            marginLeft: "1rem",
          }}
        >
          <Box
            style={{
              display: "flex",
              alignItems: "stretch",
              height: "40px",
            }}
          >
            <FormControl
              variant="outlined"
              size="small"
              className={classes.searchSelect}
              style={{
                backgroundColor: "#F0F0F0",
                minWidth: "200px",
              }}
            >
              <Select
                value={searchCriteria}
                onChange={handleSearchCriteriaChange}
                style={{ outline: "none" }}
                onFocus={(e) => (e.target.style.outline = "none")}
              >
                <MenuItem value="all">Select search by</MenuItem>
                <MenuItem value="event">Event</MenuItem>
                <MenuItem value="month">Month</MenuItem>
              </Select>
            </FormControl>

            {/* Show TextField for Event search */}
            {searchCriteria === "event" && (
              <TextField
                size="small"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                onFocus={(e) => (e.target.style.outline = "none")}
                variant="outlined"
                placeholder="Search event"
                className={classes.searchInput}
                style={{
                  flex: 1,
                  minWidth: 300,
                }}
                InputProps={{
                  style: {
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    paddingRight: searchValue ? "8px" : "0px",
                    height: "100%",
                  },
                  endAdornment: searchValue ? (
                    <InputAdornment position="end">
                      <Tooltip title="Clear search" aria-label="clear">
                        <IconButton
                          size="small"
                          onClick={() => setSearchValue("")}
                          style={{
                            padding: "4px",
                            marginRight: "4px",
                          }}
                        >
                          <Clear
                            fontSize="small"
                            style={{
                              cursor: "pointer",
                              color: "dimgray",
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ) : null,
                }}
              />
            )}

            {/* Show TextField for Month search (same UI as Event, different criteria) */}
            {searchCriteria === "month" && (
              <TextField
                size="small"
                value={searchValue}
                onChange={(e) => handleMonthInputChange(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                onFocus={(e) => (e.target.style.outline = "none")}
                variant="outlined"
                placeholder="Search month (e.g. January)"
                className={classes.searchInput}
                style={{
                  flex: 1,
                  minWidth: 300,
                }}
                InputProps={{
                  style: {
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    paddingRight: searchValue ? "8px" : "0px",
                    height: "100%",
                  },
                  endAdornment: searchValue ? (
                    <InputAdornment position="end">
                      <Tooltip title="Clear search" aria-label="clear">
                        <IconButton
                          size="small"
                          onClick={() => handleMonthInputChange("")}
                          style={{
                            padding: "4px",
                            marginRight: "4px",
                          }}
                        >
                          <Clear
                            fontSize="small"
                            style={{
                              cursor: "pointer",
                              color: "dimgray",
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ) : null,
                }}
              />
            )}

            {/* Placeholder for "all" - no input field */}
            {searchCriteria === "all" && (
              <TextField
                size="small"
                disabled
                value=""
                variant="outlined"
                placeholder="Select search criteria"
                className={classes.searchInput}
                style={{
                  flex: 1,
                  minWidth: 300,
                }}
                InputProps={{
                  style: {
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    height: "100%",
                  },
                }}
              />
            )}

            <IconButton
              onClick={handleSearch}
              disabled={
                searchCriteria === "all" || 
                (searchCriteria === "event" && !searchValue.trim()) || 
                (searchCriteria === "month" && !searchValue.trim()) ||
                loading
              }
              style={{
                backgroundColor: "#EB5E28",
                color: "white",
                borderRadius: 0,
                borderTopRightRadius: "4px",
                borderBottomRightRadius: "4px",
                border: "1px solid #000",
                borderLeft: "none",
                padding: "8px 12px",
                height: "100%",
                minWidth: "48px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.backgroundColor = "#d35400";
                }
              }}
              onMouseLeave={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.backgroundColor = "#EB5E28";
                }
              }}
            >
              <Search style={{ fontSize: "20px" }} />
            </IconButton>
          </Box>

          {/* Save Filters and Clear All Filters */}
          <Button
            variant="contained"
            disableElevation
            disableRipple
            disableFocusRipple
            className={`${classes.stylebutton} dashboard-save-filter-btn`}
            onClick={handleSaveFilters}
            disabled={
              searchCriteria === "all" || 
              (searchCriteria === "event" && !searchValue.trim()) || 
              (searchCriteria === "month" && !searchValue.trim())
            }
            style={{
              marginLeft: "16px",
              marginRight: "16px",
            }}
          >
            Save Filters
          </Button>

          <Typography
            variant="caption"
            style={{
              fontSize: "14px",
              color: "blue",
              marginLeft: "16px",
              marginRight: "16px",
              cursor: "pointer",
            }}
            className={classes.clearText}
            onClick={handleClearFilters}
          >
            Clear All Filters
          </Typography>
        </Grid>

        {/* Right Side - Last Synced (top) and Per Page (bottom) */}
        <Grid
          item
          xs={12}
          sm={4}
          md={4}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            justifyContent: "flex-start",
          }}
        >
          {/* Top - Last Synced and Refresh */}
          <Grid
            container
            className={classes.refreshContainerSty}
            justifyContent="flex-end"
            style={{ marginBottom: "2px" }}
          >
            <Typography variant="caption" className={clsx(classes.padR20)}>
              {`Last synced @ ${moment(lastSyncTime).format("HH:mm:ss; DD MMMM, YYYY")}`}
            </Typography>
            <Typography
              variant="caption"
              color="primary"
              className={classes.refreshSty}
              onClick={handleRefresh}
            >
              Refresh
              <Cached color="primary" style={{ fontSize: 15 }} />
            </Typography>
          </Grid>
          {/* Bottom - Per Page */}
          <Grid container justifyContent="flex-end" alignItems="center">
            <Typography variant="body2" className={classes.hgText}>
              PER PAGE
            </Typography>
            <FormControl className={classes.formPerPage} variant="outlined">
              <Select
                labelId="perpage-filter"
                id="perpage-filter-id"
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                style={{ outline: "none" }}
                onFocus={(e) => (e.target.style.outline = "none")}
                classes={{
                  outlined: classes.outlined,
                }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={15}>15</MenuItem>
                <MenuItem value={30}>30</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Grid>

      {/* History Table and Pagination Container */}
      <div style={{ display: "flex", flexDirection: "column", width: "100%", flex: 1, minHeight: 0, overflow: "hidden" }}>
        {/* History Table */}
        <TableContainer 
          component={Paper} 
          className={classes.tableContainer}
        >
        <Table className={classes.table} stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell className={`${classes.headerCell} ${classes.dateTimeCell}`}>Date and Time</TableCell>
                <TableCell className={`${classes.headerCell} ${classes.eventDescriptionCell}`}>Event Description</TableCell>
                <TableCell className={`${classes.headerCell} ${classes.statusCell}`} align="center">Project Status</TableCell>
                <TableCell className={`${classes.headerCell} ${classes.statusCell}`} align="center">Campaign Status</TableCell>
                <TableCell className={`${classes.headerCell} ${classes.statusCell}`} align="center">Batch Status</TableCell>
                <TableCell className={`${classes.headerCell} ${classes.organizationCell}`}>Organization</TableCell>
                <TableCell className={`${classes.headerCell} ${classes.roleCell}`}>Role</TableCell>
                <TableCell className={`${classes.headerCell} ${classes.usernameCell}`}>Username</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" style={{ padding: "40px" }}>
                    <CircularProgress />
                    <Typography variant="body2" style={{ marginTop: "10px" }}>
                      Loading project history...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" style={{ padding: "40px", color: "red" }}>
                    <Typography variant="body2">{error}</Typography>
                  </TableCell>
                </TableRow>
              ) : historyEvents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" style={{ padding: "40px" }}>
                    <Typography variant="body2" style={{ color: "#666" }}>
                      {searchCriteria === "all" 
                        ? "No history events found for this project"
                        : `No history events found matching your search criteria${
                            searchCriteria === "event" && searchValue 
                              ? `: "${searchValue}" in Event`
                              : searchCriteria === "month" && searchValue
                              ? `: ${searchValue}`
                              : ""
                          }`
                      }
                    </Typography>
                    <Typography variant="caption" style={{ display: "block", marginTop: "8px", color: "#999" }}>
                      Try changing your search criteria or clearing filters
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                historyEvents.map((row) => {
                  // Split date and time
                  const [date, time] = row.dateTime ? row.dateTime.split(' ') : ['', ''];
                  return (
                    <TableRow key={row.id}>
                      <TableCell className={`${classes.tableCell} ${classes.dateTimeCell}`}>
                        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.4' }}>
                          <span>{date || row.dateTime}</span>
                          {time && <span style={{ fontSize: '0.875rem', color: '#666' }}>{time}</span>}
                        </div>
                      </TableCell>
                      <TableCell className={`${classes.tableCell} ${classes.eventDescriptionCell}`}>{row.eventDescription}</TableCell>
                      <TableCell className={`${classes.tableCell} ${classes.statusCell}`} align="center">
                        <Chip
                          label={row.projectStatus}
                          className={classes.statusChip}
                          style={{ backgroundColor: getStatusColor(row.projectStatus) }}
                          size="small"
                        />
                      </TableCell>
                      <TableCell className={`${classes.tableCell} ${classes.statusCell}`} align="center">
                        <Chip
                          label={row.campaignStatus}
                          className={classes.statusChip}
                          style={{ backgroundColor: getStatusColor(row.campaignStatus) }}
                          size="small"
                        />
                      </TableCell>
                      <TableCell className={`${classes.tableCell} ${classes.statusCell}`} align="center">
                        <Chip
                          label={row.batchStatus}
                          className={classes.statusChip}
                          style={{ backgroundColor: getStatusColor(row.batchStatus) }}
                          size="small"
                        />
                      </TableCell>
                      <TableCell className={`${classes.tableCell} ${classes.organizationCell}`}>{row.organization}</TableCell>
                      <TableCell className={`${classes.tableCell} ${classes.roleCell}`}>{row.role}</TableCell>
                      <TableCell className={`${classes.tableCell} ${classes.usernameCell}`}>{row.username}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 0 && (
          <div className={classes.paginationContainer} style={{ flexShrink: 0 }}>
            <PaginationInput
              totalPages={totalPages}
              handlePageChange={handlePageChange}
              page={currentPage}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectHistory;

